<template>
  <container :isModal="false" :loading="loading">
    <template v-slot:body>
      <form @submit.prevent="submit">
        <fieldset :disabled="loading" class="login-wrapper">
          <div class="login-card">
            <div class="login-logo">
              <img class="img-fluid main-logo" alt="GovPass AET" />
            </div>

            <div class="login-welcome">
              <h1 class="login-title">Bem-vindo</h1>
              <p class="login-subtitle">Acesse sua conta para continuar</p>
            </div>

            <div class="login-fields">
              <div class="login-field">
                <input-with-label
                  iconClass="pi pi-user"
                  label="Usuário"
                  v-slot="l"
                  :required="true"
                >
                  <InputText
                    v-model="credenciais.login"
                    :id="l.id"
                    :required="l.required"
                    v-focus
                    autocomplete="username"
                  />
                </input-with-label>
              </div>
              <div class="login-field">
                <input-with-label
                  iconClass="pi pi-lock"
                  label="Senha"
                  v-slot="l"
                  :required="true"
                >
                  <Password
                    v-model="credenciais.senha"
                    :id="l.id"
                    :required="l.required"
                    :feedback="false"
                    autocomplete="current-password"
                    toggleMask
                  />
                </input-with-label>
              </div>
            </div>

            <div class="login-turnstile">
              <div ref="turnstileRef"></div>
            </div>

            <div class="login-actions">
              <Button
                type="submit"
                class="p-button-rounded login-button"
                label="Entrar"
                :loading="loading"
                :disabled="!turnstileToken"
                icon="pi pi-sign-in"
              />
            </div>

            <div class="login-footer">
              <router-link
                :to="{ name: 'recover-request' }"
                class="login-forgot-link"
              >
                Esqueci minha senha
              </router-link>
            </div>
          </div>
        </fieldset>
      </form>
    </template>
  </container>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  ref,
  onMounted,
  onBeforeUnmount
} from "vue";
import { useRouter } from "vue-router";
import { useAuth, TURNSTILE_VERIFIED } from "../../modules/auth";
import { CredencialPayload } from "../../modules/business/common/commonBusiness";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface LoginState {
  credenciais: CredencialPayload;
  loading: boolean;
}

export default defineComponent({
  setup() {
    const router = useRouter();
    const turnstileRef = ref<HTMLElement>();
    const turnstileToken = ref<string | null>(null);
    let widgetId: string | undefined;

    const state = reactive<LoginState>({
      credenciais: {},
      loading: false
    });

    const renderTurnstile = () => {
      if (window.turnstile && turnstileRef.value) {
        widgetId = window.turnstile.render(turnstileRef.value, {
          sitekey: "0x4AAAAAAC21H5ax1d8XlZhC",
          callback: (token: string) => {
            turnstileToken.value = token;
          },
          "expired-callback": () => {
            turnstileToken.value = null;
          },
          "error-callback": () => {
            turnstileToken.value = null;
          }
        });
      }
    };

    onMounted(() => {
      const interval = setInterval(() => {
        if (window.turnstile?.render) {
          clearInterval(interval);
          renderTurnstile();
        }
      }, 200);
      setTimeout(() => clearInterval(interval), 10000);
    });

    onBeforeUnmount(() => {
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    });

    const submit = () => {
      if (!turnstileToken.value) return;
      state.loading = true;
      state.credenciais.turnstileToken = turnstileToken.value;
      useAuth()
        .login(state.credenciais)
        .then(() => {
          sessionStorage.setItem(TURNSTILE_VERIFIED, "true");
          return router.push({ name: "home" });
        })
        .then(() => {
          state.loading = false;
        })
        .catch(() => {
          state.loading = false;
          if (widgetId && window.turnstile) {
            window.turnstile.reset(widgetId);
            turnstileToken.value = null;
          }
        });
    };
    return {
      submit,
      turnstileRef,
      turnstileToken,
      ...toRefs(state)
    };
  },
  components: {}
});
</script>

<style scoped lang="scss">
.login-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: $space-4;
}

.login-card {
  background: $bg-surface;
  border-radius: $radius-xl;
  box-shadow: $shadow-xl;
  padding: $space-10 $space-8;
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.login-logo {
  display: flex;
  justify-content: center;
  margin-bottom: $space-6;

  img {
    max-height: 48px;
    width: auto;
  }
}

.login-welcome {
  margin-bottom: $space-8;
}

.login-title {
  font-size: $font-size-2xl;
  font-weight: $font-weight-bold;
  color: $text-primary;
  margin: 0 0 $space-2 0;
}

.login-subtitle {
  font-size: $font-size-base;
  color: $text-secondary;
  margin: 0;
}

.login-fields {
  display: flex;
  flex-direction: column;
  gap: $space-5;
  text-align: left;
}

.login-field {
  width: 100%;
}

.login-turnstile {
  display: flex;
  justify-content: center;
  margin-top: $space-6;
}

.login-actions {
  margin-top: $space-6;
}

.login-button {
  width: 100%;
}

.login-footer {
  margin-top: $space-6;
}

.login-forgot-link {
  font-size: $font-size-sm;
  color: $primary;
  text-decoration: none;
  font-weight: $font-weight-medium;
  transition: color $transition-fast;

  &:hover {
    color: $primary-hover;
    text-decoration: underline;
  }
}
</style>
